import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { accelerationTrace, axisTicks } from '../../domain/cars/accelerationTrace';
import { carName } from '../../domain/cars/carCatalogue';
import { formatSpecNumber } from '../../domain/cars/carProperties';
import {
    compareFrame,
    HEADROOM,
    linearProjector,
    polyline,
    SERIES_CLASS,
} from './comparePlot';
import useWideStage from '../../application/motion/useWideStage';

/**
 * Both road tests, on one pair of axes (SPEC-008 PR-8.5 D-20, D-21, PR-8.14).
 *
 * THESE ARE THE ONLY MEASURED FIGURES ON THIS SCREEN, and that is the whole
 * reason the band exists. Everything else here is a model: the race is a
 * simulation tuned until each car reproduces its own published 0-100, and the
 * chart inside it draws what that simulation did. This draws what two magazines
 * timed, with two cars, on two days — so a reader who only ever opens this
 * screen has seen a real number before they are shown a computed one.
 *
 * It was removed under PR-8.11 on the argument that the live chart had taken
 * over its vocabulary, and what that cost was written down at the time rather
 * than glossed. This is that cost being paid back; the live chart keeps its own
 * job, which is telling a reader what happened in the run they just watched.
 *
 * SPEED AGAINST TIME, which is how a road test publishes it and how the 8-bit
 * car screens drew it — the same axes, the same dotted grid and the same
 * monospaced figures in the margin as the band on each car's own card. The live
 * chart's axis is distance, for the reason it states: its curve has to stay
 * under the car drawing it. Nothing here is moving, so nothing here has to.
 *
 * WHAT IT DOES NOT BORROW from the single-car band is the figure printed on
 * every mark. That rule (SPEC-004 D-17a) is affordable for one series of two to
 * four points and is not for two of them: eight labels on one 320-unit plot
 * collide with each other and with both curves, and the two cars whose points
 * already crowd their own chart would be crowding the other car's as well. The
 * marks stay, the figures move to the table underneath, and the legend carries
 * the one figure per car that a reader needs to read the picture — where each
 * line ENDS, because two tests that stopped in different places are otherwise
 * two curves of unexplained different lengths.
 *
 * A car with no published road test is NAMED rather than silently absent (D-21):
 * one line on a pair of axes with no explanation reads as a bug. Eleven of the
 * twenty-four have no test of their own variant, so this is the ordinary case
 * rather than the edge one.
 *
 * The `--accel` modifier carries no style and is not decoration: this screen now
 * draws TWO plates with identical class names, and without it every assertion
 * about "the chart" — here and in the browser suite — would silently be an
 * assertion about whichever one the document happened to put first.
 */

/**
 * The extent both series are drawn against.
 *
 * The measurements' own, never the top speed's — the same choice
 * `accelerationTrace` makes for one car and for the same reason: an axis scaled
 * to a 324 km/h ceiling nobody timed spends two thirds of its width on a stretch
 * of curve that does not exist. Taking the MAXIMUM of the two puts both tests on
 * one scale, which is the only thing that makes two curves in one box a
 * comparison rather than two pictures.
 */
function sharedExtent(traces) {
    return {
        seconds: Math.max(...traces.map((trace) => trace.duration)),
        kmh: Math.max(...traces.map((trace) => trace.ceiling)),
    };
}

/** One car's line in the legend: its colour, its name, and where its test ended. */
function Reading({ car, index, trace }) {
    const t = useTranslate();
    const { languageCode } = useLocale();

    if (!trace) {
        return null;
    }

    const last = trace.measured[trace.measured.length - 1];

    return (
        <li className={`compare-chart__key ${SERIES_CLASS[index]}`}>
            <span className="compare-chart__swatch" aria-hidden="true" />
            <span className="compare-chart__key-name">{carName(car)}</span>
            <span className="compare-chart__key-note">
                {t('compare.accel.reached', {
                    speed: formatSpecNumber(last.kmh, { languageCode }),
                    seconds: formatSpecNumber(last.seconds, { languageCode, fractionDigits: 1 }),
                })}
            </span>
        </li>
    );
}

export default function CompareAccelerationChart({ cars }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    // The drawing box, not the layout: `compare.css` decides how wide the plate
    // is and this decides how many units are drawn across it. The two step at the
    // same width on purpose (`useWideStage`).
    const frame = compareFrame(useWideStage());
    const traces = cars.map((car) => accelerationTrace(car));
    const drawn = traces.filter(Boolean);
    const missing = cars.filter((car, index) => !traces[index]);

    // A pair with no test between them gets a sentence rather than an empty
    // frame, exactly as the value band does: a labelled pair of axes with
    // nothing on it reads as a chart that failed to load.
    if (drawn.length === 0) {
        return (
            <p className="compare-chart__absent compare-chart__absent--accel">
                {t('compare.accel.neither', { cars: cars.map(carName).join(t('compare.and')) })}
            </p>
        );
    }

    const extent = sharedExtent(drawn);
    const project = linearProjector(frame, {
        width: extent.seconds * HEADROOM,
        height: extent.kmh * HEADROOM,
    });
    // Keyed to the MEASURED extent rather than to the headroom, so the frame is
    // roomy and no gridline claims a second nobody timed.
    const timeTicks = axisTicks(extent.seconds);
    const speedTicks = axisTicks(extent.kmh);
    const seconds = (value) => formatSpecNumber(value, { languageCode, fractionDigits: 0 });
    const speed = (value) => formatSpecNumber(value, { languageCode });
    const tenths = (value) => formatSpecNumber(value, { languageCode, fractionDigits: 1 });

    return (
        <figure className="compare-chart compare-chart--accel">
            <p className="compare-chart__axis-label" aria-hidden="true">
                {t('cars.accel.speedAxis')}
            </p>

            <svg
                className="compare-chart__plot"
                viewBox={frame.viewBox}
                aria-hidden="true"
                focusable="false"
            >
                {/* The grid first, so every mark is drawn over it. */}
                <g className="compare-chart__grid">
                    {speedTicks.map((tick) => (
                        <line key={`speed-${tick}`} x1={frame.plot.left} x2={frame.right} y1={project.y(tick)} y2={project.y(tick)} />
                    ))}
                    {timeTicks.map((tick) => (
                        <line key={`time-${tick}`} x1={project.x(tick)} x2={project.x(tick)} y1={frame.plot.top} y2={frame.floor} />
                    ))}
                </g>

                <path className="compare-chart__axis" d={frame.axisPath} />

                <g className="compare-chart__ticks">
                    {speedTicks.map((tick) => (
                        <text key={`speed-label-${tick}`} x={frame.plot.left - 4} y={project.y(tick) + 3} textAnchor="end">
                            {speed(tick)}
                        </text>
                    ))}
                    {timeTicks.map((tick) => (
                        <text key={`time-label-${tick}`} x={project.x(tick)} y={frame.view.height - 4} textAnchor="middle">
                            {seconds(tick)}
                        </text>
                    ))}
                </g>

                {traces.map((trace, index) => (trace ? (
                    <g className={`compare-chart__series ${SERIES_CLASS[index]}`} key={cars[index].id + String(index)}>
                        {/* Solid for the first car, dashed for the second, which
                            is this sheet's redundant encoding rather than a
                            statement about the data. Both lines are the same
                            KIND of claim: an interpolation between measurements,
                            monotone so it cannot overshoot them
                            (`accelerationTrace.js`), and the marks on top are
                            what a magazine actually timed. */}
                        <path
                            className="compare-chart__curve"
                            d={polyline(
                                trace.curve.map((sample) => ({ x: sample.seconds, y: sample.kmh })),
                                project,
                            )}
                        />
                        {trace.measured.map((point) => (
                            <circle
                                className="compare-chart__point"
                                key={`${point.seconds}-${point.kmh}`}
                                cx={project.x(point.seconds)}
                                cy={project.y(point.kmh)}
                                r={3.5}
                            />
                        ))}
                    </g>
                ) : null))}
            </svg>

            <p className="compare-chart__axis-label compare-chart__axis-label--end" aria-hidden="true">
                {t('cars.accel.timeAxis')}
            </p>

            <ul className="compare-chart__legend">
                {cars.map((car, index) => (
                    <Reading car={car} index={index} key={car.id + String(index)} trace={traces[index]} />
                ))}
            </ul>

            {/* Every figure the plot marks, readable by ear. A curve is not, at
                any level of labelling, which is why the SVG above is hidden
                rather than described. */}
            <table className="visually-hidden">
                <caption>{t('compare.accel.tableCaption')}</caption>
                <thead>
                    <tr>
                        <th scope="col">{t('compare.table.figure')}</th>
                        <th scope="col">{t('cars.accel.speedAxis')}</th>
                        <th scope="col">{t('cars.accel.timeAxis')}</th>
                    </tr>
                </thead>
                <tbody>
                    {traces.flatMap((trace, index) => (trace?.measured ?? []).map((point) => (
                        <tr key={`${cars[index].id}-${point.seconds}`}>
                            <td>{carName(cars[index])}</td>
                            <td>{t('cars.accel.speedValue', { speed: speed(point.kmh) })}</td>
                            <td>{t('cars.accel.timeValue', { seconds: tenths(point.seconds) })}</td>
                        </tr>
                    )))}
                </tbody>
            </table>

            {/* Printed rather than tucked into a title attribute: these figures
                are citable or they are decoration, and a title attribute does
                not exist on a phone. */}
            <figcaption className="compare-chart__source">
                {traces.map((trace, index) => (trace ? (
                    <span className="compare-chart__sourced" key={cars[index].id + String(index)}>
                        {t('compare.accel.measuredBy', {
                            car: carName(cars[index]),
                            source: trace.source,
                        })}
                        {' '}
                    </span>
                ) : null))}
                {missing.length > 0
                    ? t('compare.accel.missing', { car: missing.map(carName).join(t('compare.and')) })
                    : null}
                {' '}
                {t('compare.accel.basis')}
            </figcaption>
        </figure>
    );
}
